import { useUserContext } from '../context/UserContext';
import {
  canAccessPayroll,
  hasPayrollRole,
  hasPayrollPermission,
  getCustomerRole,
  getEffectiveUserRole,
  isPayrollAdmin,
  isHRManager,
  isAccountant,
  isPayrollViewer,
  canAccessPayrollFeature,
  getUserDisplayRole,
  canManageEmployees,
  canViewCrossBranchData,
  canAccessAdminFeatures,
  getUserAccessibleFeatures,
  getAllowedBatchStatuses,
  filterBatchesByRole,
  canCreateBatch,
  canApproveHR,
  canApproveAccountant,
  canManageBankDetails,
  canManageFunding,
  canViewWallet,
  canManageDeductions,
  canManageSettlements,
  canViewSettlements,
  canUpdatePayrollSettings,
  canViewPayrollSettings,
} from '../utils/accessControl';

/**
 * Custom hook for access control
 * Provides easy access to all access control functions with current user context
 */
export const useAccessControl = () => {
  const { user, customerRoles } = useUserContext();

  return {
    // User info
    user,
    userRole: user?.role,
    userBranch: user?.branch_name,
    userPayrollRoles: user?.payroll_roles || [],
    userPayrollPermissions: user?.payroll_permissions || [],
    customerRoles,
    
    // Basic access checks
    canAccessPayroll: () => canAccessPayroll(user),
    canManageEmployees: () => canManageEmployees(user),
    canViewCrossBranchData: () => canViewCrossBranchData(user),
    canAccessAdminFeatures: () => canAccessAdminFeatures(user),
    
    // Payroll role checks (enhanced with customer roles)
    hasPayrollRole: (role) => hasPayrollRole(user, role),
    hasPayrollPermission: (permission) => hasPayrollPermission(user, permission),
    getCustomerRole: () => getCustomerRole(customerRoles),
    getEffectiveUserRole: () => getEffectiveUserRole(user, customerRoles),
    isPayrollAdmin: () => isPayrollAdmin(user, customerRoles),
    isHRManager: () => isHRManager(user, customerRoles),
    isAccountant: () => isAccountant(user, customerRoles),
    isPayrollViewer: () => isPayrollViewer(user, customerRoles),
    
    // Payroll feature access (enhanced with customer roles)
    canAccessPayrollFeature: (feature) => canAccessPayrollFeature(user, feature, customerRoles),
    
    // Utility functions
    getUserDisplayRole: () => getUserDisplayRole(user, customerRoles),
    getUserAccessibleFeatures: () => getUserAccessibleFeatures(user),
    
    // Batch filtering and permissions
    getAllowedBatchStatuses: () => getAllowedBatchStatuses(user, customerRoles),
    filterBatchesByRole: (batches) => filterBatchesByRole(batches, user, customerRoles),
    canCreateBatch: () => canCreateBatch(user, customerRoles),
    canApproveHR: () => canApproveHR(user, customerRoles),
    canApproveAccountant: () => canApproveAccountant(user, customerRoles),
    canManageBankDetails: () => canManageBankDetails(user, customerRoles),
    canManageFunding: () => canManageFunding(user, customerRoles),
    canViewWallet: () => canViewWallet(user, customerRoles),
    canManageDeductions: () => canManageDeductions(user, customerRoles),
    canManageSettlements: () => canManageSettlements(user, customerRoles),
    canViewSettlements: () => canViewSettlements(user, customerRoles),
    canUpdatePayrollSettings: () => canUpdatePayrollSettings(user, customerRoles),
    canViewPayrollSettings: () => canViewPayrollSettings(user, customerRoles),
    
    // Quick access to common checks
    isMainBranchUser: () => canAccessPayroll(user),
    isCompanyAdmin: () => user?.role === 'company_admin',
    isBranchUser: () => {
      const effectiveRole = getEffectiveUserRole(user, customerRoles);
      return effectiveRole === 'branch_user';
    },
  };
};

export default useAccessControl;